// client/src/components/OptInModal.tsx
//
// Modal post-primera-importación. Renderizado vía React Portal al document.body
// para evitar issues de stacking context.

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { User } from '@supabase/supabase-js';
import { useT } from '../lib/i18n';
import { useDataSharingConsent } from '../hooks/useDataSharingConsent';

interface Props {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
}

export function OptInModal({ user, isOpen, onClose }: Props) {
  const { t } = useT();
  const { grant, updating, error } = useDataSharingConsent(user);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleAccept = async () => {
    const ok = await grant();
    if (ok) {
      setSubmitted(true);
      setTimeout(() => {
        onClose();
        setSubmitted(false);
      }, 800);
    }
  };

  const handleDecline = () => {
    onClose();
  };

  const modalContent = (
    <div
      className="optin-modal-backdrop"
      onClick={e => {
        if (e.target === e.currentTarget) handleDecline();
      }}
    >
      <div className="optin-modal" role="dialog" aria-modal="true">
        <div className="optin-modal-header">
          <span className="optin-modal-icon" aria-hidden>
            ◈
          </span>
          <h2 className="optin-modal-title">{t('optInModal.title')}</h2>
        </div>

        <div className="optin-modal-body">
          <p className="optin-modal-intro">{t('optInModal.intro')}</p>

          <ul className="optin-modal-bullets">
            <li>
              <span className="optin-bullet-icon">✓</span>
              <span>{t('optInModal.bullet1')}</span>
            </li>
            <li>
              <span className="optin-bullet-icon">✓</span>
              <span>{t('optInModal.bullet2')}</span>
            </li>
            <li>
              <span className="optin-bullet-icon">✓</span>
              <span>{t('optInModal.bullet3')}</span>
            </li>
            <li>
              <span className="optin-bullet-icon">✓</span>
              <span>{t('optInModal.bullet4')}</span>
            </li>
          </ul>

          <p className="optin-modal-fineprint">{t('optInModal.fineprint')}</p>

          {error && <div className="optin-modal-error">{error}</div>}
        </div>

        <div className="optin-modal-footer">
          <button
            className="btn btn-secondary optin-btn-decline"
            onClick={handleDecline}
            disabled={updating || submitted}
          >
            {t('optInModal.declineBtn')}
          </button>
          <button
            className="btn btn-primary optin-btn-accept"
            onClick={handleAccept}
            disabled={updating || submitted}
          >
            {submitted
              ? t('optInModal.acceptedBtn')
              : updating
              ? t('optInModal.savingBtn')
              : t('optInModal.acceptBtn')}
          </button>
        </div>

        <p className="optin-modal-changelater">{t('optInModal.changeLater')}</p>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
